import {
  createContext,
  useContext,
  type CSSProperties,
  type PropsWithChildren,
} from "react";
import { NodeContext } from "@nkitaa/builder-engine";
import DOMPurify from "dompurify";
import { IconCircleCheck, IconCircleX } from "@tabler/icons-react";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import { t } from "../locale";
import { wallPartsToIso } from "../../lib/utils/datetime-tz";
import { safeMediaSrc } from "../lib/safe-media-src";
import { IMAGE_CLEAR_SENTINEL } from "../lib/image-upload";
import { revealClass, type WBReveal } from "./reveal";

export const WB_VALIDATE_EVENT = "wb:validate-fields";
export const WB_FIELD_CHANGED_EVENT = "wb:field-changed";

/**
 * The guest's current form context (session.user + live typed values, with the
 * companion overlay merged while the companion modal is open) — the same merged
 * object {@code App.nodeVisibilityFn} builds. Provided once by App so a single
 * field input can evaluate its field-definition ConditionGroup (visibility /
 * editable / per-option) against sibling values live, without each input
 * subscribing to WB_FIELD_CHANGED_EVENT on its own. Flat field-name → value;
 * pass to {@code evaluateConditionGroup} wrapped as
 * {@code { ...ctx, additionalFields: ctx }} so `additionalFields.*` keys resolve.
 */
export const LiveGuestValuesContext = createContext<Record<string, unknown>>({});

/**
 * Submit state of the owning form (DataInput / Register / Login / Contact)
 * for the TextChild / Button it mounts.
 *
 * Audit 2026-09-23 (4b): props on `<Element>` are SEEDS — the engine reads
 * them only when it CREATES a linked node; a node saved with the page keeps
 * its stored `isSubmitted:false` / `loading:false`. So the "thank you" text
 * never switched and the submit button never spun on a published site. A
 * consumer inside this provider follows the context regardless of stored
 * props — including a stored `enableSubmittedState:false`, which is itself a
 * seed (templates seed it `true`; the cockpit has no toggle for it).
 */
export type SubmitStateSnapshot = { isSubmitted: boolean; loading: boolean };
export const SubmitStateContext = createContext<SubmitStateSnapshot | null>(null);
export function useSubmitState(): SubmitStateSnapshot | null {
  return useContext(SubmitStateContext);
}

/**
 * Outcome of a registration for the success card — same seed problem as
 * above: the card's `isWaitlisted` / `waitlistPosition` arrived as JSX props
 * and were ignored once the node had been saved. Context presence wins.
 */
export type RegisterOutcome = {
  isPendingApproval: boolean;
  isWaitlisted: boolean;
  waitlistPosition: number | undefined;
};
export const RegisterOutcomeContext = createContext<RegisterOutcome | null>(null);
export const useLiveGuestValues = (): Record<string, unknown> =>
  useContext(LiveGuestValuesContext);
export const WB_BOOKING_CAPACITY_EVENT = "wb:booking-capacity";
export const WB_COMPANION_BADGE_COLORS_EVENT = "wb:companion-badge-colors";
export const WB_BOOKING_SUMMARY_EVENT = "wb:booking-summary";
export const WB_COMPANION_FIELD_LABELS_EVENT = "wb:companion-field-labels";
/** P9-F2: companion edit-modal open/close — detail {open: boolean}. While
 *  open, App evaluates visibility against the companion's own live values
 *  overlaid on the main form's (documented v1 limitation: the overlay is
 *  global while the modal is open — only the modal is interactable). */
export const WB_COMPANION_MODAL_EVENT = "wb:companion-modal";
export const WB_BOOKING_ERROR_EVENT = "wb:booking-error";
export const WB_BOOKING_REGISTER_EVENT = "wb:booking-register";
/** Session-selection (P4): dataInput dispatches this on the form after a
 *  409 `sessionFull` submit; detail = { fullProgramItemIds: string[] }.
 *  SessionGroup marks those cards sold-out and prompts a re-pick. */
export const WB_SESSION_FULL_EVENT = "wb:session-full";
/** OFFERING slot booking (W3): dataInput dispatches this on the form after
 *  a submit failed with 409 `offeringSlotConflict` (reason "conflict") or
 *  400 `offeringNotDirectlyBookable` (reason "notBookable"); detail =
 *  { programItemId, code, reason }. SessionGroup refetches that block's
 *  slots (re-seeding the selection from the guest's surviving appointment)
 *  and forces a conscious re-pick — mirror of WB_SESSION_FULL_EVENT. */
export const WB_OFFERING_CONFLICT_EVENT = "wb:offering-conflict";
/** dataInput dispatches this on the form after `/data-input/submit`
 *  succeeded (register AND edit paths). SessionGroup uses it to clear its
 *  per-block "explicitly removed" flags and to re-baseline its
 *  change-detection (`__offering_dirty`) at the just-persisted state —
 *  the submitted set IS the server state now. No detail payload. */
export const WB_SUBMIT_SUCCESS_EVENT = "wb:submit-success";

// Cache keys on window for late-mounting listeners (race condition fix)
const WB_CACHE_KEY = "__wb_event_cache" as const;
type WBEventCache = { [key: string]: unknown };
function getCache(): WBEventCache {
  const w = window as unknown as { [WB_CACHE_KEY]?: WBEventCache };
  if (!w[WB_CACHE_KEY]) w[WB_CACHE_KEY] = {};
  return w[WB_CACHE_KEY];
}

/** Dispatch a custom event and cache the detail for late-mounting listeners. */
export function dispatchCachedEvent(type: string, detail: unknown): void {
  getCache()[type] = detail;
  window.dispatchEvent(new CustomEvent(type, { detail }));
}

/** Read the last cached value for a custom event type (may be undefined). */
export function readCachedEvent<T>(type: string): T | undefined {
  return getCache()[type] as T | undefined;
}

/** Scan the event cache for all keys matching `prefix:*` and return their non-null values. */
export function scanCachedEvents<T>(prefix: string): { key: string; value: T }[] {
  const cache = getCache();
  const results: { key: string; value: T }[] = [];
  const search = `${prefix}:`;
  for (const key in cache) {
    if (key.startsWith(search) && cache[key] != null) {
      results.push({ key, value: cache[key] as T });
    }
  }
  return results;
}

/**
 * Where the runtime's own FALLBACK images come from (a stored page carries
 * absolute URLs of its own; these only fire for a section that never got one).
 *
 * The prod value was `https://dhpkewhw3h8nf.cloudfront.net` until 2026-08-20 —
 * a host that stopped resolving with the R2 migration (no DNS record at all),
 * so every fallback below rendered as a broken image. R2 serves the same keys
 * at assets.veltric.app, which is also what the editor bakes into pages.
 */
export const ASSET_BASE = import.meta.env.DEV
  ? "http://localhost:4566/public-assets"
  : "https://assets.veltric.app";

export const DEFAULT_TEXT = "";

export type VisibilityProps = {
  isVisible?: boolean;
};

export type Optional<T> = T | undefined;

export type RuntimeProps = Record<string, unknown> & VisibilityProps;
export type RuntimePropsWithChildren = PropsWithChildren<RuntimeProps>;
export type BorderSide = "top" | "right" | "bottom" | "left";
export type FieldSpacing = "extraSmall" | "small" | "medium" | "large" | "extraLarge";

export type SectionContainerProps = {
  sectionHeight?: Optional<number>;
  contentAlignment?: Optional<"top" | "center" | "bottom">;
  backgroundColor?: Optional<string>;
  padding?: Optional<string>;
  sectionMargin?: Optional<string>;
  border?: Optional<boolean>;
  borderColor?: Optional<string>;
  borderWidth?: Optional<string>;
  borderStyle?: Optional<string>;
  borderSides?: Optional<BorderSide[]>;
  borderRadius?: Optional<string>;
  /** Declarative scroll-reveal. Applied to the padding/background wrapper —
   *  the editor puts revealClass on its outer section div, and the CSS
   *  animation's fill-mode must NOT share an element with the framer
   *  SECTION_REVEAL_PROPS content (it would override framer's inline
   *  opacity). */
  reveal?: Optional<WBReveal>;
};

export const SECTION_REVEAL_PROPS = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: {
    duration: 0.6,
    ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
  },
};

export const CARD_HOVER_PROPS = {
  whileHover: { y: -4, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" },
  transition: {
    duration: 0.25,
    ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
  },
};

export function isVisible(v?: boolean): boolean {
  return v !== false;
}

/* ------------------------------------------------------------------ */
/*  Visibility-filter evaluation (rule-based visibility)              */
/* ------------------------------------------------------------------ */

export type SimpleFilter = {
  field: string;
  operator: "=" | "≠" | "~" | ">" | "≥" | "<" | "≤" | "∅" | "⊙";
  value: string | string[];
};

export type AdvancedFilter = {
  type: "&" | "|";
  simpleFilters: SimpleFilter[];
};

export type ParentFilter = {
  type: "&" | "|";
  simpleFilters: SimpleFilter[];
  advancedFilters: AdvancedFilter[];
};

function resolveUserFieldValue(
  user: Record<string, unknown>,
  field: string,
): unknown {
  // Direct top-level lookup
  if (Object.prototype.hasOwnProperty.call(user, field)) {
    return user[field];
  }
  // P9-F1 (P0 fix): the editor's PrivacySettings stores custom-field keys as
  // "additionalFields.X", but live form values are keyed bare ("X") and
  // session values sit nested under user.additionalFields — neither the
  // direct nor the normalized lookup could bridge the dotted form
  // ("additionalfieldsx" ≠ "x"), so rules on custom fields NEVER matched.
  // Strip the prefix and resolve the tail through the same lookup chain.
  // ⚠ PARITY: website-builder-backend's form-field-visibility port and the
  // main-frontend editor copy (sections/utils/evaluateVisibilityFilter.ts)
  // must mirror this exactly.
  if (field.startsWith("additionalFields.")) {
    return resolveUserFieldValue(user, field.slice("additionalFields.".length));
  }
  // Additional fields lookup
  const additional = user.additionalFields;
  if (additional && typeof additional === "object" && !Array.isArray(additional)) {
    const rec = additional as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(rec, field)) {
      return rec[field];
    }
    // Normalized lookup (case-insensitive, stripped non-alphanumeric)
    const norm = field.replace(/[^a-z0-9]/gi, "").toLowerCase();
    for (const [k, v] of Object.entries(rec)) {
      if (k.replace(/[^a-z0-9]/gi, "").toLowerCase() === norm) return v;
    }
  }
  // Normalized top-level lookup
  const norm = field.replace(/[^a-z0-9]/gi, "").toLowerCase();
  for (const [k, v] of Object.entries(user)) {
    if (k === "additionalFields") continue;
    if (k.replace(/[^a-z0-9]/gi, "").toLowerCase() === norm) return v;
  }
  return undefined;
}

function coerceString(value: unknown): string {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) return value.map(String).join(",");
  return String(value);
}

function evaluateSimpleFilter(
  filter: SimpleFilter,
  user: Record<string, unknown>,
): boolean {
  if (!filter.field) return true; // Empty field = no constraint
  const raw = resolveUserFieldValue(user, filter.field);
  const actual = coerceString(raw).toLowerCase();
  const expected = Array.isArray(filter.value)
    ? filter.value.map((v) => v.toLowerCase())
    : [String(filter.value ?? "").toLowerCase()];

  switch (filter.operator) {
    case "=":
      return expected.some((v) => actual === v);
    case "≠":
      return expected.every((v) => actual !== v);
    case "~":
      return expected.some((v) => actual.includes(v));
    case ">":
      return Number(actual) > Number(expected[0]);
    case "≥":
      return Number(actual) >= Number(expected[0]);
    case "<":
      return Number(actual) < Number(expected[0]);
    case "≤":
      return Number(actual) <= Number(expected[0]);
    case "∅":
      return raw === undefined || raw === null || actual === "";
    case "⊙":
      return raw !== undefined && raw !== null && actual !== "";
    default:
      return true;
  }
}

function evaluateAdvancedFilter(
  filter: AdvancedFilter,
  user: Record<string, unknown>,
): boolean {
  const results = filter.simpleFilters.map((sf) =>
    evaluateSimpleFilter(sf, user),
  );
  if (results.length === 0) return true;
  return filter.type === "&"
    ? results.every(Boolean)
    : results.some(Boolean);
}

export function evaluateVisibilityFilter(
  filter: ParentFilter | undefined,
  user: Record<string, unknown>,
): boolean {
  if (!filter) return true;

  const hasAdvanced = filter.advancedFilters?.some(
    (af) => af.simpleFilters?.length > 0,
  );
  if (!hasAdvanced) return true; // No active rules → visible

  const results = filter.advancedFilters
    .filter((af) => af.simpleFilters?.length > 0)
    .map((af) => evaluateAdvancedFilter(af, user));

  if (results.length === 0) return true;
  return filter.type === "&"
    ? results.every(Boolean)
    : results.some(Boolean);
}

export function splitTokens(value?: string): string[] {
  const normalized = nonEmptyString(value);
  if (!normalized) return [];
  return normalized.split(/\s+/).filter(Boolean);
}

export function classNames(
  ...values: Array<string | undefined | false | null>
): string | undefined {
  const combined = values
    .flatMap((value) => (value ? value.split(/\s+/) : []))
    .map((token) => token.trim())
    .filter(Boolean)
    .join(" ");
  return combined || undefined;
}

export function extractUtilityClasses(
  value: string | undefined,
  prefix: string,
): string {
  return splitTokens(value)
    .filter((token) => token.startsWith(prefix) || token.includes(`:${prefix}`))
    .join(" ");
}

export function toCssLength(value?: string): string | undefined {
  const trimmed = nonEmptyString(value);
  if (!trimmed || trimmed.includes(" ")) return undefined;
  if (trimmed === "auto") return "auto";
  if (/^-?\d+(\.\d+)?(px|rem|em|vh|vw|%)$/.test(trimmed)) return trimmed;
  return undefined;
}

export function getBorderClasses(
  border: boolean | undefined,
  borderWidth: string | undefined,
  borderStyle: string | undefined,
  borderSides?: BorderSide[],
): string {
  if (!border) return "";

  // Defensive: these are typed `string` but older saved sites can persist
  // non-string values; guard so `.trim()` never throws.
  const width = typeof borderWidth === "string" ? borderWidth.trim() : undefined;
  const style = typeof borderStyle === "string" ? borderStyle.trim() : undefined;
  const sides = borderSides;

  if (!sides || sides.length === 0 || sides.length === 4) {
    return classNames(width, style) ?? "";
  }

  const sideMap: Record<BorderSide, string> = {
    top: "t",
    right: "r",
    bottom: "b",
    left: "l",
  };

  if (!width) {
    return (
      classNames(...sides.map((side) => `border-${sideMap[side]}`), style) ?? ""
    );
  }

  const widthSuffix = width === "border" ? "" : width.replace("border", "");
  return (
    classNames(
      ...sides.map((side) => `border-${sideMap[side]}${widthSuffix}`),
      style,
    ) ?? ""
  );
}

export function parseAlignment(
  value?: "left" | "right" | "center",
): CSSProperties["alignItems"] {
  if (value === "right") return "flex-end";
  if (value === "center") return "center";
  return "flex-start";
}

export function parseFormMaxWidth(
  width?: number,
  maxWidth?: number,
): string | undefined {
  const widthPx = Number(width);
  const maxWidthVw = Number(maxWidth);
  if (
    Number.isFinite(widthPx) &&
    Number.isFinite(maxWidthVw) &&
    maxWidthVw > 0
  ) {
    return `min(${widthPx}px, ${maxWidthVw}vw)`;
  }
  if (Number.isFinite(widthPx)) return `${widthPx}px`;
  if (Number.isFinite(maxWidthVw) && maxWidthVw > 0) return `${maxWidthVw}vw`;
  return undefined;
}

export function resolveFieldSpacing(fieldSpacing?: string): string {
  switch (fieldSpacing as FieldSpacing | undefined) {
    case "extraSmall":
      return "0.5rem";
    case "small":
      return "0.75rem";
    case "large":
      return "1.5rem";
    case "extraLarge":
      return "2rem";
    case "medium":
    default:
      return "1rem";
  }
}

export function unwrapMaybeQuoted(value: unknown): string {
  // Runtime data is JSON — a prop typed as `string` (e.g. a Hero's
  // borderRadius) can actually arrive as a number/boolean from older saved
  // sites. Coerce defensively so `.trim()` never throws ("n.trim is not a
  // function").
  const str = typeof value === "string" ? value : String(value ?? "");
  const trimmed = str.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1).trim();
  }
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (typeof parsed === "string") return parsed.trim();
  } catch {
    // Keep original if value is not JSON.
  }
  return trimmed;
}

export function nonEmptyString(value?: unknown): string | undefined {
  if (!value) return undefined;
  const unwrapped = unwrapMaybeQuoted(value);
  return unwrapped.length > 0 ? unwrapped : undefined;
}

export function stopBuilderSelection(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

export function notifyAuthChanged() {
  window.dispatchEvent(new CustomEvent("wb:auth-changed"));
}

export function formatOverviewValue(value: unknown): string {
  if (Array.isArray(value)) return value.map((item) => String(item)).join(", ");
  if (typeof value === "boolean") return value ? t("yes") : t("no");
  if (value === undefined || value === null) return "-";
  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : "-";
}

export function parseUnknownBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
    return undefined;
  }
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  if (
    normalized === "true" ||
    normalized === "1" ||
    normalized === "yes" ||
    normalized === "on"
  )
    return true;
  if (
    normalized === "false" ||
    normalized === "0" ||
    normalized === "no" ||
    normalized === "off"
  )
    return false;
  return undefined;
}

export function mapOverviewLabelToVerification(value?: string): string | undefined {
  if (!value) return value;
  const normalized = value.trim().toLowerCase();
  if (normalized === "overview" || normalized === "übersichtsseite") {
    return t("verificationPage");
  }
  return value;
}

export function sanitizeImageSrc(src: string | undefined): string | undefined {
  const trimmed = nonEmptyString(src);
  if (!trimmed) return trimmed;
  const stripped = trimmed.replace(/^(?:undefined|null)(?:\/|$)/, "") || undefined;
  if (!stripped) return undefined;
  // Audit 11-NEW-1: drop attacker-controlled non-http(s) schemes
  // (javascript:, vbscript:, data:text/html, …) before the URL
  // reaches `<img src>` or a CSS `background-image: url(…)` slot.
  // Relative paths inherit https: via safeMediaSrc's URL base.
  return safeMediaSrc(stripped);
}

export function sectionMinHeight(sectionHeight?: number, appBarTransparent?: boolean): string | undefined {
  if (!sectionHeight || sectionHeight <= 0) return undefined;
  if (sectionHeight >= 100) return appBarTransparent ? "100vh" : "calc(100vh - 75px)";
  return `${sectionHeight}vh`;
}

export function alignFlex(
  value?: "top" | "center" | "bottom",
): CSSProperties["justifyContent"] {
  if (value === "top") return "flex-start";
  if (value === "bottom") return "flex-end";
  return "center";
}

export function parseHeroWidth(width: "100%" | "75%" | "50%" | "33%" | "25%"): string {
  switch (width) {
    case "75%":
      return "w-full wb-md:w-3/4";
    case "50%":
      return "w-full wb-md:w-1/2";
    case "33%":
      return "w-full wb-md:w-1/3";
    case "25%":
      return "w-full wb-md:w-1/4";
    case "100%":
    default:
      return "w-full";
  }
}

export function parseHeroOppositeWidth(
  width: "100%" | "75%" | "50%" | "33%" | "25%",
): string {
  switch (width) {
    case "75%":
      return "w-full wb-md:w-1/4";
    case "50%":
      return "w-full wb-md:w-1/2";
    case "33%":
      return "w-full wb-md:w-2/3";
    case "25%":
      return "w-full wb-md:w-3/4";
    case "100%":
    default:
      return "w-full";
  }
}

export function parseContentAlignmentClass(
  contentAlignment?: "top" | "center" | "bottom",
): string {
  return contentAlignment === "center"
    ? "justify-center"
    : contentAlignment === "bottom"
      ? "justify-end"
      : "justify-start";
}

export function getGridClasses(
  gridColumns: "2" | "3" | "4" | "5" | "6",
  layoutWidth: "100%" | "75%" | "50%" | "33%" | "25%",
): string {
  const cols = Number.parseInt(gridColumns, 10);
  let maxCols = cols;
  if (layoutWidth === "75%") maxCols = Math.min(cols, 2);
  else if (layoutWidth === "50%") maxCols = Math.min(cols, 3);
  else if (layoutWidth === "33%" || layoutWidth === "25%")
    maxCols = Math.min(cols, 4);

  if (maxCols <= 1) return "grid grid-cols-1";
  if (maxCols === 2) return "grid grid-cols-1 wb-sm:grid-cols-2";
  if (maxCols === 3)
    return "grid grid-cols-1 wb-sm:grid-cols-2 wb-md:grid-cols-3";
  if (maxCols === 4)
    return "grid grid-cols-1 wb-sm:grid-cols-2 wb-md:grid-cols-3 wb-lg:grid-cols-4";
  if (maxCols === 5)
    return "grid grid-cols-1 wb-sm:grid-cols-2 wb-md:grid-cols-3 wb-lg:grid-cols-5";
  if (maxCols === 6)
    return "grid grid-cols-1 wb-sm:grid-cols-2 wb-md:grid-cols-3 wb-lg:grid-cols-6";
  return "grid grid-cols-1";
}

export type AppBarSectionKey = "logo" | "links" | "social";

export function normalizeAppBarSectionOrder(value: unknown): AppBarSectionKey[] {
  const fallback: AppBarSectionKey[] = ["logo", "links", "social"];
  if (!Array.isArray(value)) return fallback;
  const normalized = value.filter(
    (item): item is AppBarSectionKey =>
      item === "logo" || item === "links" || item === "social",
  );
  return normalized.length > 0 ? normalized : fallback;
}

/**
 * Audit 7.12 r2 B2-2: DOMPurify's default drops `target`, so a link the
 * organiser set to open in a new tab — the registration form's privacy-notice
 * row links the organiser's privacy URL that way — opened in the SAME tab and
 * a guest lost the half-filled form. `target` is now let through, but only as
 * `_blank` on a link, which then always carries rel="noopener noreferrer"
 * (no `window.opener` for the linked page, no referrer). Any other target
 * (named frames, _top, _parent, _self) and a target on a non-link are removed.
 *
 * The hook is registered for THIS call only and removed right after
 * (sanitize is synchronous), so DOMPurify's shared instance — the Embed
 * section's `sanitizeEmbedHtml` among others — keeps its default behaviour.
 */
const forceSafeBlankTarget = (node: Element) => {
  if (!node.hasAttribute || !node.hasAttribute("target")) return;
  const isLink = node.tagName === "A" || node.tagName === "AREA";
  const target = (node.getAttribute("target") ?? "").trim().toLowerCase();
  if (isLink && target === "_blank") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  } else {
    node.removeAttribute("target");
  }
};

export function sanitizeHtml(value: string): string {
  DOMPurify.addHook("afterSanitizeAttributes", forceSafeBlankTarget);
  try {
    return DOMPurify.sanitize(value, { ADD_ATTR: ["target"] });
  } finally {
    DOMPurify.removeHook("afterSanitizeAttributes", forceSafeBlankTarget);
  }
}

export function ThemeSectionContainer({
  children,
  sectionHeight,
  contentAlignment,
  backgroundColor,
  padding,
  sectionMargin,
  border,
  borderColor,
  borderWidth,
  borderStyle,
  borderSides,
  borderRadius,
  reveal,
}: PropsWithChildren<SectionContainerProps>) {
  const { currentColorPalette, currentRounding } = useWBTheme();
  // The Craft node id doubles as the section's DOM anchor target so nav links
  // of the form "/slug#<nodeId>" can scroll to it. scroll-mt clears the sticky
  // AppBar. Matches the editor's EditableWrapper.
  const nodeId = useContext(NodeContext);
  const roundedClasses = extractUtilityClasses(
    borderRadius ?? currentRounding,
    "rounded",
  );
  const borderClasses = getBorderClasses(
    border,
    borderWidth,
    borderStyle,
    borderSides,
  );

  return (
    <section
      id={nodeId || undefined}
      className={classNames(
        "websitebuilder w-full flex justify-center scroll-mt-24",
        sectionMargin,
      )}
    >
      <div
        className={classNames(
          "w-full flex",
          padding,
          borderClasses,
          roundedClasses,
          revealClass(reveal),
        )}
        style={{
          backgroundColor,
          minHeight: sectionMinHeight(sectionHeight),
          justifyContent: alignFlex(contentAlignment),
          border: border ? undefined : "none",
          borderColor: border
            ? (borderColor ?? currentColorPalette.secondary)
            : undefined,
          borderRadius: roundedClasses
            ? undefined
            : toCssLength(borderRadius ?? currentRounding),
          boxSizing: "border-box",
        }}
      >
        {children}
      </div>
    </section>
  );
}

export function textAlign(textAlign?: string): CSSProperties["textAlign"] {
  if (
    textAlign === "left" ||
    textAlign === "right" ||
    textAlign === "center" ||
    textAlign === "justify"
  ) {
    return textAlign;
  }
  return "left";
}

export type SubmitState = {
  loading: boolean;
  error?: string;
  success?: string;
  warning?: string;
};

export function StatusMessage({ state }: { state: SubmitState }) {
  if (state.error) {
    return (
      <div
        role="alert"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          marginTop: "0.75rem",
          padding: "0.75rem 1rem",
          backgroundColor: "#fef2f2",
          border: "1px solid #fecaca",
          borderRadius: "0.5rem",
          color: "#b91c1c",
          fontSize: "0.875rem",
          lineHeight: "1.25rem",
        }}
      >
        <IconCircleX size={20} style={{ flexShrink: 0 }} />
        <span>{state.error}</span>
      </div>
    );
  }
  return (
    <>
      {state.success && (
        <div
          role="status"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            marginTop: "0.75rem",
            padding: "0.75rem 1rem",
            backgroundColor: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: "0.5rem",
            color: "#166534",
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
          }}
        >
          <IconCircleCheck size={20} style={{ flexShrink: 0 }} />
          <span>{state.success}</span>
        </div>
      )}
      {state.warning && (
        <div
          role="alert"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            marginTop: "0.75rem",
            padding: "0.75rem 1rem",
            backgroundColor: "#fffbeb",
            border: "1px solid #fde68a",
            borderRadius: "0.5rem",
            color: "#92400e",
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
          }}
        >
          <IconCircleX size={20} style={{ flexShrink: 0 }} />
          <span>{state.warning}</span>
        </div>
      )}
    </>
  );
}

export type CollectedFieldValues = {
  values: Record<string, unknown>;
  companions: Array<Record<string, unknown>>;
  checkboxes: Array<{
    text: string;
    required: boolean;
    checked: boolean;
    inputFieldVariable?: string;
  }>;
  /** 2026-10-01: set only on DataInput's own mount/step capture taken before
   *  the visitor touched anything. Such a draft is a DOM read, not an answer —
   *  it can be taken in the very commit a widget only *scheduled* its session
   *  prefill, so it may say `false` for a stored `emailOptIn: true`. The
   *  Checkboxes prefill lets the session beat a pristine draft (formGroups.tsx).
   *  `collectFieldValues` never sets it; any capture after an input, change,
   *  step move or submit drops it. */
  pristine?: true;
};

/**
 * Convert a wall-clock `<input type="datetime-local">` value (e.g.
 * `"2026-05-20T14:30"` — interpreted in `eventTz`) into a UTC ISO string for
 * the backend. Reads only the first two `:`-separated time components so
 * Safari's optional `:ss` suffix is dropped without touching the original
 * string. Empty input → "".
 */
function wallDateTimeLocalToUtcIso(value: string, eventTz: string): string {
  if (!value) return "";
  const [datePart, timePart] = value.split("T");
  if (!datePart || !timePart) return value; // give up gracefully
  const [yStr, mStr, dStr] = datePart.split("-");
  const [hStr, minStr] = timePart.split(":");
  if (!yStr || !mStr || !dStr || !hStr || !minStr) return value;
  const year = parseInt(yStr, 10);
  const month = parseInt(mStr, 10) - 1;
  const day = parseInt(dStr, 10);
  const hour = parseInt(hStr, 10);
  const minute = parseInt(minStr, 10);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return value;
  }
  return wallPartsToIso({ year, month, day, hour, minute }, eventTz);
}

export function collectFieldValues(
  form: HTMLFormElement,
  eventTz: string,
): CollectedFieldValues {
  const values: Record<string, unknown> = {};
  const companions: Array<Record<string, unknown>> = [];
  const companionDraft: Record<number, Record<string, unknown>> = {};
  const checkboxes: Array<{
    text: string;
    required: boolean;
    checked: boolean;
    inputFieldVariable?: string;
  }> = [];

  const nodes = form.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("[data-field-variable], [data-checkbox-text]");

  nodes.forEach((el) => {
    const checkboxText = el.dataset.checkboxText;
    if (
      checkboxText !== undefined &&
      el instanceof HTMLInputElement &&
      el.type === "checkbox"
    ) {
      const checkboxFieldVariable = nonEmptyString(
        el.dataset.checkboxFieldVariable ?? el.dataset.fieldVariable,
      );
      checkboxes.push({
        text: checkboxText,
        required: el.dataset.required === "true",
        checked: el.checked,
        ...(checkboxFieldVariable
          ? { inputFieldVariable: checkboxFieldVariable }
          : {}),
      });
      if (checkboxFieldVariable) {
        values[checkboxFieldVariable] = el.checked;
      }
      return;
    }

    const variable = el.dataset.fieldVariable;
    if (!variable) return;

    let value: unknown;
    if (el instanceof HTMLInputElement && el.type === "checkbox") {
      value = el.checked;
    } else if (el instanceof HTMLSelectElement && el.multiple) {
      value = Array.from(el.selectedOptions).map((opt) => opt.value);
    } else {
      value = el.value;
    }

    // Locale-aware numeric inputs (DOUBLE / CURRENCY) carry the CANONICAL
    // dot-decimal in `data-canonical` (computed on change from the locale
    // draft). Submit that, not the displayed "3.519,5", so the backend's
    // Number()/Double.parseDouble gets a clean value. Only these marked nodes
    // are touched — STRING fields with separators are never reinterpreted.
    if (
      el.dataset.numericType &&
      typeof el.dataset.canonical === "string"
    ) {
      value = el.dataset.canonical;
    }

    // IMAGE upload widgets (marked `data-image-pending`) carry the staged
    // upload's pendingId; submit the `{__pendingUpload: <id>}` sentinel the
    // worker's validator/finalize step expects.
    //  - "" = UNTOUCHED → the key is OMITTED from the submission entirely
    //    (absent = keep the stored image; submitting "" would let the
    //    worker treat it as a value for the field).
    //  - IMAGE_CLEAR_SENTINEL = the guest pressed the explicit remove
    //    button on a stored image → submit the literal `null` the worker
    //    treats as a clear (delete R2 object + release storage).
    if (el.dataset.imagePending !== undefined && typeof value === "string") {
      if (value === "") return; // untouched — omit the key
      value =
        value === IMAGE_CLEAR_SENTINEL
          ? null
          : { __pendingUpload: value };
    }

    // BOOLEAN selects (marked `data-boolean-type`) carry "true"/"false"
    // strings; submit a real boolean — the backend validator requires
    // `typeof value === 'boolean'`. The empty "not selected" option stays
    // "" so the not-present/empty-drop handling is unchanged.
    if (
      el instanceof HTMLSelectElement &&
      el.dataset.booleanType !== undefined &&
      typeof value === "string" &&
      value !== ""
    ) {
      value = value === "true";
    }

    // tz-aware post-processing: visible DATE_TIME inputs ship wall-clock
    // strings; convert to UTC ISO using the event's tz before submission.
    // DATE inputs ship "YYYY-MM-DD" — date-only, no tz semantics. Hidden
    // companion bridge inputs (no `data-tz-mode`) are already UTC ISO from
    // the companion modal save handler; pass through.
    if (
      el.dataset.tzMode === "datetime-local" &&
      typeof value === "string" &&
      value !== ""
    ) {
      value = wallDateTimeLocalToUtcIso(value, eventTz);
    }

    const isCompanion = el.dataset.companion === "true";
    if (isCompanion) {
      const idx = Number(el.dataset.companionIndex ?? 0);
      if (!companionDraft[idx]) companionDraft[idx] = {};
      companionDraft[idx][variable] = value;
      return;
    }

    values[variable] = value;
  });

  Object.keys(companionDraft)
    .map((k) => Number(k))
    .sort((a, b) => a - b)
    .forEach((idx) => {
      const companion = companionDraft[idx];
      if (!companion) return;
      const hasNonEmpty = Object.values(companion).some((v) => {
        if (v === undefined || v === null) return false;
        if (typeof v === "string") return v.trim().length > 0;
        return true;
      });
      if (hasNonEmpty) companions.push(companion);
    });

  return { values, companions, checkboxes };
}

export const DataInputNavigationContext = createContext<
  | {
      goBack: () => void;
      goNext: () => void;
    }
  | undefined
>(undefined);

export function useDataInputNavigation() {
  return useContext(DataInputNavigationContext);
}

export const CompanionIndexOverrideContext = createContext<number | undefined>(
  undefined,
);
export function useCompanionIndexOverride(): number | undefined {
  return useContext(CompanionIndexOverrideContext);
}

export const DataInputReadOnlyContext = createContext(false);
export const DataInputVerificationContext = createContext<CollectedFieldValues | null>(
  null,
);
export const DataInputDraftContext = createContext<CollectedFieldValues | null>(null);

export const AppBarTransparentContext = createContext(false);
export function useAppBarTransparent() {
  return useContext(AppBarTransparentContext);
}

export function useDataInputReadOnly() {
  return useContext(DataInputReadOnlyContext);
}

export function useDataInputVerificationSnapshot() {
  return useContext(DataInputVerificationContext);
}

export function useDataInputDraftSnapshot() {
  return useContext(DataInputDraftContext);
}

export function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function withoutAdditionalFields(
  record: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!record) return {};
  const next = { ...record };
  delete next.additionalFields;
  return next;
}

export function asOverviewStringValue(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  return String(value);
}

export function resolveRecordValueByField(
  record: Record<string, unknown>,
  field: string,
): unknown {
  if (Object.prototype.hasOwnProperty.call(record, field)) {
    return record[field];
  }

  const normalizedField = normalizeLookupKey(field);
  for (const [key, value] of Object.entries(record)) {
    if (normalizeLookupKey(key) === normalizedField) {
      return value;
    }
  }

  return undefined;
}

function normalizeLookupKey(value: string): string {
  return value.replace(/[^a-z0-9]/gi, "").toLowerCase();
}
